import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import './Auth.css';
import CarSizeOptions from './CarSizeOptions';
import PasswordField from './PasswordField';
import PasswordChecklist from './PasswordChecklist';
import { checkPassword, passwordProblem } from '../utils/passwordPolicy';

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [carColor, setCarColor] = useState('');
  const [carType, setCarType] = useState('');
  // Google sign-up in progress — the token is held while the user completes the fields this page
  // already shows, so it can be re-submitted without sending them back through Google.
  const [googleIdToken, setGoogleIdToken] = useState(null);
  const navigate = useNavigate();

  // 2026-09-26: this used to refuse to start until plate, colour and type were filled, which
  // defeated the point of one-tap sign-up, and it had no handling for the server's 428 at all — so
  // being asked for details surfaced as a raw error. The server asks for whatever is missing and now
  // suggests a username too, collected afterwards using the fields this page already shows.
  const handleGoogleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse.credential;
    try {
      const response = await fetch('/api/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          idToken,
          plateNumber: plateNumber || null,
          carColor: carColor || null,
          carType: carType || null,
          username: username || null
        }),
      });

      const raw = await response.text();
      let data;
      try { data = JSON.parse(raw); } catch { data = { message: raw }; }

      if (response.status === 428) {
        setGoogleIdToken(idToken);
        if (data.needsUsername && data.suggestedUsername && !username) setUsername(data.suggestedUsername);
        alert('Almost there — please complete the details below, then click "Complete Google sign-up".');
        return;
      }
      if (!response.ok && (data.code === 'username_taken' || data.code === 'username_invalid')) {
        setGoogleIdToken(idToken);
      }

      if (response.ok) {
        localStorage.setItem('token', data.token);
        sessionStorage.setItem('welcomeMessage', `Welcome, ${data.username}!`);
        // Navigate to the map/dashboard.
        // 2026-10-14: was navigate('/'), which worked only while "/" was the React splash screen and
        // redirected a token-holder onward. "/" is now the static landing page, so this dropped a
        // user who had just signed up onto the marketing site.
        navigate('/dashboard');
        // Trigger a reload or state update if necessary, 
        // but App.js should pick up the token on its own or through some emitter
        window.location.reload(); 
      } else {
        alert(`Google registration failed: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error during Google registration:', error);
      alert('An error occurred during Google registration.');
    }
  };

  const handleGoogleError = () => {
    console.error('Google registration failed');
    alert('Google registration failed. Please try again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 2026-09-26: was username/email/password only, which contradicted this form's own `required`
    // attributes and the mobile screen, and let someone register with no car details at all — while
    // /api/auth/google has always demanded plate, colour and type. All six, consistently.
    if (!username || !password || !email || !plateNumber || !carColor || !carType) {
      alert('All fields are required for registration.');
      return;
    }
    // The button is disabled until the checklist is all green; this covers a submit that lands anyway.
    const weak = passwordProblem(password, { username, email });
    if (weak) {
      alert(weak);
      return;
    }
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, email, password, plateNumber, carColor, carType }),
      });

      if (response.ok) {
        alert('Registration successful! Please log in.');
        navigate('/login');
      } else {
        const errorData = await response.text();
        alert(`Registration failed: ${errorData}`);
      }
    } catch (error) {
      console.error('Error during registration:', error);
      alert('An error occurred during registration.');
    }
  };

  return (
    <>
      <div className="auth-page-wrapper">
        <div className="logo-container">
          {/* A plain <a>, not <Link>: "/" is the static landing page and Caddy only rewrites it on
              a real document request — a router pushState would resolve "/" inside the app. */}
          <a href="/" className="brand-home" aria-label="Back to the Udal home page">
            <img src="/udal-logo-small.png" alt="" className="auth-logo" />
            <h1 className="logo">Udal</h1>
          </a>
          <h2 className="tagline">It notices when you <span className="highlight">park</span>. It shares when you <span className="highlight">leave</span>.</h2>
        </div>
        <div className="auth-top-background"></div>
        <div className="spacer"></div>
        <div className="auth-container">
          <h2>Register</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username <span className="required-mark">*</span></label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            {/* Hidden once Google has identified the user: the account will have no local password
                and its email comes from the verified Google token. They are also `required`, so
                leaving them on screen would block the form from ever submitting. */}
            {!googleIdToken && (
              <>
                <div className="form-group">
                  <label htmlFor="email">Email <span className="required-mark">*</span></label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password <span className="required-mark">*</span></label>
                  <PasswordField
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <PasswordChecklist password={password} username={username} email={email} />
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="plateNumber">Plate Number <span className="required-mark">*</span></label>
              <input
                type="text"
                id="plateNumber"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="carColor">Car Color <span className="required-mark">*</span></label>
              <input
                type="text"
                id="carColor"
                value={carColor}
                onChange={(e) => setCarColor(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="carType">Car size <span className="required-mark">*</span></label>
              <select id="carType" value={carType} onChange={(e) => setCarType(e.target.value)} required>
                <CarSizeOptions />
              </select>
            </div>
            {googleIdToken ? (
              <button type="button" onClick={() => handleGoogleSuccess({ credential: googleIdToken })}>
                Complete Google sign-up
              </button>
            ) : (
              <button type="submit" disabled={!checkPassword(password, { username, email }).ok} style={checkPassword(password, { username, email }).ok ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}>Register</button>
            )}
            {/* GDPR Art. 13: said where the data is actually collected, not on a settings page. */}
            <p className="policy-notice">
              By creating an account you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
            <div className="social-login-separator">
              <span>OR</span>
            </div>
            <div className="google-login-container" style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="signup_with"
                shape="rectangular"
                width="100%"
              />
            </div>
          </form>
          <p>
            Already have an account? <Link to="/login">Login here</Link>
          </p>
          <p className="back-home"><a href="/">&larr; Back to home</a></p>
        </div>
        <footer className="App-footer">
          <p>&copy; 2026 UDAL LIMITED</p>
        </footer>
      </div>
    </>
  );
};

export default Register;
